import {
  Component,
  forwardRef,
  ViewChild,
  ViewEncapsulation,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnInit,
  NgZone,
  AfterViewInit
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { NgbDateAdapter, NgbDateParserFormatter, NgbInputDatepicker, NgbDatepickerI18n, NgbDateStruct, NgbDate }
from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { Moment } from 'moment';
import { NgbMomentAdapter } from './ngb-moment-adapter';
import { DatePickerLocalizer } from './date-picker-localizer';
import { DateFormatHelper } from './date-format-helper';
import * as _ from 'lodash';
import { DatePickerConfiguration } from './date-picker.model';

@Component({
  selector: 'ssg-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.less'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NgbDateParserFormatter,
      useExisting: DatePickerComponent
    },
    DatePickerLocalizer,
    {
      provide: NgbDatepickerI18n,
      useExisting: DatePickerLocalizer
    },
    NgbMomentAdapter,
    {
      provide: NgbDateAdapter,
      useExisting: NgbMomentAdapter
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true
    }
  ]
})
export class DatePickerComponent implements ControlValueAccessor, OnInit {
  @ViewChild(NgbInputDatepicker, { static: true }) dpInput: NgbInputDatepicker;
  @Output() invalidDateChanged = new EventEmitter<any>();
  @Input() justify = 'left';
  @Input() nullable = false;
  @Input() hasValidationError = false;
  @Input() config: DatePickerConfiguration = null;

  private _invalidDate = false;
  private _defaultMinDate = moment('0001-01-01');

  errorMessage: string = null;
  warningMessage: string = null;

  constructor(
    private readonly _formatHelper: DateFormatHelper,
    private readonly zone: NgZone) { }

  ngOnInit() {
    if (this.config && this.initialized()) {
      this.dpInput.markDisabled = (date: NgbDate) => this.config.isDisabled(date);
    }
  }

  get minDate(): Moment {
    return this.initialized() && this.dpInput.minDate ? NgbMomentAdapter.toMoment(this.dpInput.minDate) : this._defaultMinDate;
  }

  @Input() set minDate(date: Moment) {
    this.zone.run(() => {
      if (this.initialized()) {
        this.dpInput.minDate = NgbMomentAdapter.fromMoment(date);
      }
    });
  }

  get invalidDate(): boolean {
    return this._invalidDate;
  }

  set invalidDate(value: boolean) {
    if (this._invalidDate !== value) {
      this.zone.run(() => this._invalidDate = value);
      this.invalidDateChanged.emit(value);
    }
  }

  private onChange(value: any): void {
 return;
}

  writeValue(value: Moment) {
    if (this.initialized()) {
      this.dpInput.writeValue(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
    if (this.initialized()) {
      this.dpInput.registerOnChange(fn);
    }
  }

  registerOnTouched(fn: any): void {
    if (this.initialized()) {
      this.dpInput.registerOnTouched(fn);
    }
  }

  validateString(date: string): void {
    this.zone.run(() => {
    this.warningMessage = null;
    if (!this._formatHelper.validate(date, this.nullable)) {
        this.errorMessage = 'COMMON.ENTER_VALID_DATE';
        this.invalidDate = true;
    } else {
      this.validateDate(this._formatHelper.parse(date));
    }
  });
  }

  validateDate(date: Moment): void {
    this.zone.run(() => {
      this.warningMessage = null;
      if (date) {
        this.invalidDate = date.isBefore(this.minDate);
        if (this.invalidDate) {
          this.errorMessage = 'COMMON.PRIOR_TO_MIN_DATE';
        } else if (this.config && this.config.isDisabled(NgbMomentAdapter.fromMoment(date))) {
        if (this.config.enforceManualEntry) {
          this.invalidDate = true;
          this.errorMessage = this.config.disabledMessage;
        } else {
          this.warningMessage = this.config.disabledMessage;
        }
      }
      } else {
          this.invalidDate = !this.nullable;
      }
    });
  }

  parse(value: string): NgbDateStruct {
    this.validateString(value);
    const parsed = this._formatHelper.parse(value);

    return this.ToDateStruct(parsed);
  }

  format(date: NgbDateStruct): string {
    this.zone.run(() => this.invalidDate = false);
    if (!date) {
 return '';
}

    const momentDate = moment.utc({ year: date.year, month: date.month - 1, day: date.day });
    this.validateDate(momentDate);

    return this._formatHelper.format(momentDate);
  }

  isWhitelisted(date: NgbDateStruct) {
    return this.config && this.config.isWhitelisted(date as NgbDate);
  }

  private ToDateStruct(date: Moment): NgbDateStruct {
    return { day: date.date(), month: date.month() + 1, year: date.year() };
  }

  toggle() {
    this.zone.run(() => {
      if (this.initialized()) {
        this.dpInput.toggle();
      }
    });
  }

  close() {
    this.zone.run(() => {
      if (this.initialized()) {
        this.dpInput.close();
      }
    });
  }

  initialized(): boolean {
    return this.dpInput !== undefined;
  }
}
